import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Avatar from "../UI/Avatar";
import Breadcrumb from "../UI/Breadcrumb";
import Text from "../UI/Text";
import IconButton from "../UI/IconButton";
import DropdownMenu from "../UI/DropdownMenu";
import styles from "./HeaderTenant.module.css";
// @ts-expect-error pure js
import { useModal } from "../../hooks/use-modal.jsx";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
// @ts-expect-error pure js
import PartForm from "../UI/PartForm";
// @ts-expect-error pure js
import TenantHeaderForm from "./PartForms/TenantHeaderForm";
// @ts-expect-error pure js
import { sanitizeValues, toInitialValuesFromSchema } from "../../models/utils";
// @ts-expect-error pure js
import AddNoteTenant from "./AddNoteTenant";
import {
  headerTenantSchema,
  TENANT_INITIAL_VALUE,
  TenantStatus,
  // @ts-expect-error pure js
} from "../../models/tenant_old.js";
// @ts-expect-error pure js
import { useTenantProfiles } from "../../hooks/use-profiles-indiv";
// @ts-expect-error pure js
import { ProfileInviteStatus, ProfileStatus } from "../../models/profile";
import {
  IconArchive,
  IconArchiveOff,
  IconMailForward,
  IconNote,
  IconPencil,
  IconUserCheck,
  IconUserOff,
  IconUserShare,
} from "@tabler/icons-react";
import { useToast } from "../UI/Toast/useToast";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import Title from "../UI/Title.tsx";
import AplTag from "./Apl/AplTag";
import type { Tenant } from "../../models/tenant";
import type { Property } from "../../models/property";
import type { Profile } from "../Profile/types";
import { getInitials, isProfileInvitable } from "../Profile/profileUtils";
import InviteLocataireModal from "../Profile/InviteLocataireModal";
import RevokeInviteDialog from "../Profile/RevokeInviteDialog";
import VideoTutorialsButton from "../VideoTutorials/VideoTutorialsButton";
import type { TutorialPage } from "../VideoTutorials/videoTutorials";
import useActiveTab from "../../hooks/use-active-tab";

const TAB_TUTORIALS: Partial<Record<string, TutorialPage>> = {
  "vue-ensemble": "locataire",
  "historique-transactions": "locataire-paiements",
};

interface HeaderTenantProps {
  tenant: Tenant;
  property?: Property;
}

export default function HeaderTenant({ tenant, property }: HeaderTenantProps) {
  const nav = useNavigate();
  const toast = useToast();
  const { updateTenant, deleteTenant } = useTenants();
  const activeTab = useActiveTab("tenant-show", "vue-ensemble");
  const tutorialPage = TAB_TUTORIALS[activeTab];

  async function changeStatus(status: number, errorTitle: string) {
    try {
      await updateTenant(tenant.id, { status });
    } catch (error) {
      toast.error({ title: errorTitle, description: handleAPIError(error) });
    }
  }
  const {
    structProfiles: profiles,
    inviteProfiles,
    revokeInviteProfiles,
  } = useTenantProfiles(tenant?.id);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isRevokeOpen, setIsRevokeOpen] = useState(false);

  const [
    HeaderTenantFormModal,
    closeHeaderTenantFormModal,
    openHeaderTenantFormModal,
  ] = useModal();
  const [AddNoteModal, closeAddNoteModal, openAddNoteModal] = useModal();

  const initialValues = toInitialValuesFromSchema(
    headerTenantSchema,
    tenant,
    TENANT_INITIAL_VALUE,
  );

  const hasNote = Boolean(tenant.notes?.trim());

  const activeProfiles =
    profiles?.filter(
      (p: Profile) => p.status !== ProfileStatus.Archive.status,
    ) || [];
  const displayedProfiles = activeProfiles.slice(0, 2);
  const remainingCount = activeProfiles.length - 2;

  const titulaires: Profile[] = activeProfiles.filter(
    (p: Profile) => !p.garantor_of && !p.legal_representative_of,
  );
  const isActif = tenant.status === TenantStatus.Actif.status;
  const canInvite = isActif && titulaires.some(isProfileInvitable);
  // Avec un seul titulaire, l'en-tête porte aussi son statut d'accès
  // (renvoi / révocation) ; en colocation, chaque carte profil garde le sien.
  const soleTitulaire = titulaires.length === 1 ? titulaires[0] : null;
  const soleInvited =
    soleTitulaire?.status_invite === ProfileInviteStatus.INVITED;
  const soleConnected =
    soleTitulaire?.status_invite === ProfileInviteStatus.CONNECTED;

  return (
    <>
      <AddNoteModal size="medium">
        <AddNoteTenant tenant={tenant} onClose={closeAddNoteModal} />
      </AddNoteModal>
      <InviteLocataireModal
        open={isInviteOpen}
        onOpenChange={setIsInviteOpen}
        profiles={titulaires}
        onInvite={inviteProfiles}
        resend={soleInvited}
      />
      {soleTitulaire && (
        <RevokeInviteDialog
          open={isRevokeOpen}
          onOpenChange={setIsRevokeOpen}
          isConnected={soleConnected}
          onRevoke={() => revokeInviteProfiles([soleTitulaire.id])}
        />
      )}
      <HeaderTenantFormModal>
        <PartForm
          partForm={<TenantHeaderForm />}
          deleteHandler={
            tenant.status === TenantStatus.Archive.status
              ? () => {
                  deleteTenant(tenant.id);
                  nav("/bailleur/tenants?tab-tenant-list=archives");
                }
              : undefined
          }
          initialValues={initialValues}
          validationSchema={headerTenantSchema}
          submitHandler={(values: Record<string, unknown>) =>
            updateTenant(tenant.id, sanitizeValues(values))
          }
          closeModal={closeHeaderTenantFormModal}
        />
      </HeaderTenantFormModal>

      <div className={styles.header}>
        <div className={styles.breadcrumb}>
          <Breadcrumb
            items={
              tenant.status === TenantStatus.Candidat.status ||
              tenant.status === TenantStatus.CandidatArchive.status
                ? [
                    { label: "Tableau de bord", to: "/bailleur" },
                    { label: "Candidats", to: "/bailleur/candidats" },
                    { label: tenant.nom },
                  ]
                : [
                    { label: "Tableau de bord", to: "/bailleur" },
                    { label: "Locataires", to: "/bailleur/tenants" },
                    { label: tenant.nom },
                  ]
            }
          />
        </div>
        <div className={styles.background}>
          {TenantStatus.CandidatArchive.status !== tenant.status && (
            <div className={styles.actions}>
              {canInvite && (
                <IconButton
                  icon={IconMailForward}
                  requiresWrite
                  onClick={() => setIsInviteOpen(true)}
                  data-tour="locataire-inviter"
                  label={
                    titulaires.length > 1
                      ? "Inviter les locataires"
                      : "Inviter le locataire"
                  }
                />
              )}
              {isActif && soleTitulaire && (soleInvited || soleConnected) && (
                <DropdownMenu
                  align="end"
                  trigger={
                    <IconButton
                      icon={soleConnected ? IconUserCheck : IconUserShare}
                      label={soleConnected ? "Connecté" : "Invité"}
                    />
                  }
                >
                  {soleInvited && (
                    <DropdownMenu.Item
                      label="Renvoyer l'invitation"
                      icon={IconMailForward}
                      onClick={() => setIsInviteOpen(true)}
                      requiresWrite
                    />
                  )}
                  <DropdownMenu.Item
                    label={
                      soleConnected
                        ? "Révoquer l'accès"
                        : "Révoquer l'invitation"
                    }
                    icon={IconUserOff}
                    variant="danger"
                    onClick={() => setIsRevokeOpen(true)}
                    requiresWrite
                  />
                </DropdownMenu>
              )}
              <IconButton
                icon={IconPencil}
                label="Modifier"
                onClick={openHeaderTenantFormModal}
              />
              <div className={styles.noteButton}>
                <IconButton
                  icon={IconNote}
                  label={hasNote ? "Une note est enregistrée" : "Notes"}
                  onClick={openAddNoteModal}
                />
                {hasNote && <span className={styles.noteDot} />}
              </div>
              {tenant.status === TenantStatus.Actif.status && (
                <IconButton
                  icon={IconArchive}
                  label="Archiver"
                  color="warning"
                  requiresWrite
                  onClick={() =>
                    changeStatus(
                      TenantStatus.Archive.status,
                      "Impossible d'archiver le locataire",
                    )
                  }
                />
              )}
              {tenant.status === TenantStatus.Archive.status && (
                <IconButton
                  icon={IconArchiveOff}
                  label="Réactiver"
                  color="warning"
                  requiresWrite
                  onClick={() =>
                    changeStatus(
                      TenantStatus.Actif.status,
                      "Impossible de réactiver le locataire",
                    )
                  }
                />
              )}
            </div>
          )}
        </div>

        <div className={styles.profileSection}>
          <div className={styles.avatars}>
            {displayedProfiles.map((profile: Profile) => (
              <Avatar
                key={profile.id}
                initials={getInitials(profile)}
                size="lg"
                variant="blue"
              />
            ))}
            {remainingCount > 0 && (
              <div className={styles.moreAvatars}>
                <Text weight="bold">{remainingCount}+</Text>
              </div>
            )}
          </div>

          <div className={styles.info}>
            <div className={styles.nameRow}>
              <Title variant="hecto" weight="bold">
                {tenant.nom}
              </Title>
              <AplTag tenant={tenant} />
              {tutorialPage && (
                <div className={styles.tutorials}>
                  <VideoTutorialsButton page={tutorialPage} />
                </div>
              )}
            </div>

            {property && (
              <Link
                to={`/bailleur/properties/${property.id}`}
                className={styles.propertyLink}
              >
                <Text weight="bold" as="span">
                  {property.nom} :
                </Text>{" "}
                <Text as="span">
                  {property.rue}, {property.cp} {property.ville}
                </Text>
              </Link>
            )}

            {tenant.reference_dossier && (
              <Text variant="deci">
                Référence de dossier : {tenant.reference_dossier}
              </Text>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
